import { useState } from "react";
import { motion, useAnimationControls } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { API, setToken } from "@/lib/api";
import { Monogram } from "@/components/Monogram";

const formatError = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || "").join(" ");
  return "Something went wrong. Please try again.";
};

export const PasswordGate = ({ onUnlock }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const shake = useAnimationControls();

  const submit = async (e) => {
    e.preventDefault();
    if (!password || loading) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/gate/enter`, { password });
      setToken(data.token);
      toast.success("Welcome to the archive");
      onUnlock();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || "Incorrect password");
      shake.start({ x: [0, -12, 12, -8, 8, -4, 0], transition: { duration: 0.5 } });
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      data-testid="password-gate"
      className="fixed inset-0 z-[80] flex flex-col items-center justify-center bg-[#FAFAF7] px-6"
      exit={{ opacity: 0, scale: 1.04, filter: "blur(6px)" }}
      transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
    >
      <motion.div
        animate={{ scale: [1, 1.07, 1], opacity: [0.85, 1, 0.85] }}
        transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut" }}
        className="text-[#B08D2E]"
      >
        <Monogram size={300} />
      </motion.div>

      <motion.h1
        initial={{ opacity: 0, y: 18 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.25, duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
        className="font-display mt-8 text-3xl font-light tracking-[0.35em] text-[#131311] sm:text-4xl"
      >
        ROY A SMITH
      </motion.h1>
      <motion.p
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.9 }}
        className="mt-3 font-mono text-[10px] uppercase tracking-[0.4em] text-zinc-500"
      >
        Private Archive — By Invitation
      </motion.p>

      <motion.form
        onSubmit={submit}
        animate={shake}
        initial={{ opacity: 0, y: 16 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.65, duration: 0.8 }}
        className="mt-12 flex w-full max-w-xs flex-col items-center gap-6"
      >
        <input
          data-testid="password-input"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="····"
          autoFocus
          autoComplete="off"
          className="w-full border-b border-black/20 bg-transparent pb-3 text-center font-mono text-xl tracking-[0.5em] text-[#131311] outline-none transition-colors placeholder:text-zinc-300 focus:border-[#9A7B24]"
        />
        {error && (
          <p data-testid="password-error-message" className="font-mono text-xs tracking-widest text-red-500">
            {error}
          </p>
        )}
        <button
          data-testid="password-submit-btn"
          type="submit"
          disabled={loading}
          className="group relative mt-2 overflow-hidden rounded-full border border-[#B08D2E]/50 px-10 py-3 font-mono text-xs uppercase tracking-[0.3em] text-[#9A7B24] transition-colors duration-500 hover:text-white disabled:opacity-50"
        >
          <span className="absolute inset-0 -translate-x-full bg-[#B08D2E] transition-transform duration-500 ease-out group-hover:translate-x-0" />
          <span className="relative">{loading ? "Opening…" : "Unlock"}</span>
        </button>
      </motion.form>

      <p className="absolute bottom-8 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">
        royasmith.com
      </p>
    </motion.div>
  );
};
